/**
 * Přehrávač ukázky videa (VZHLED 5).
 * Tabulový poster + velké play tlačítko + badge s ukázkovou lekcí.
 *
 * Zdroj streamu řeší WP6, do té doby je `src` prázdný a přehrání zobrazí
 * placeholder hlášku. Jakmile WP6 dodá signed URL, stačí předat `src` a
 * komponenta přehraje inline `<video>` s ovládáním.
 */

import { useRef, useState } from 'react';
import { Info, Play } from 'lucide-react';
import { toast } from 'sonner';
import { formatClock } from '../lib/format';

export default function VideoPlayer({
  title,
  durationS,
  src,
  poster,
}: {
  title: string;
  durationS?: number;
  src?: string;
  poster?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const badge = durationS ? `UKÁZKOVÁ LEKCE · ${formatClock(durationS)}` : 'UKÁZKOVÁ LEKCE';

  const handlePlay = () => {
    if (!src) {
      // WP6 zatím nedodává stream, ukázku přehrajeme, až bude obsah k dispozici.
      toast.info('Ukázku videa připravujeme', {
        description: 'Přehrávání ukázek zprovozníme brzy. Katalog i osnovu si zatím prohlédnete.',
      });
      return;
    }
    setPlaying(true);
    // Přehrání spustíme po vykreslení <video>.
    window.setTimeout(() => void videoRef.current?.play(), 0);
  };

  return (
    <div>
      <div className="board relative aspect-video overflow-hidden rounded-card shadow-card">
        {playing && src ? (
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            controls
            playsInline
            className="h-full w-full bg-board-deep"
          >
            <track kind="captions" />
          </video>
        ) : (
          <button
            type="button"
            onClick={handlePlay}
            aria-label={`Přehrát ukázkovou lekci, ${title}`}
            className="group absolute inset-0 grid place-items-center"
          >
            <div className="grid-paper pointer-events-none absolute inset-0 opacity-70" />
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <p className="select-none font-mono text-lg font-bold tracking-wide text-accent md:text-2xl">
                3x&nbsp;−&nbsp;7&nbsp;=&nbsp;2x&nbsp;+&nbsp;5
              </p>
              <p className="mt-1 select-none text-xs text-chalk/70 md:text-sm">{title}</p>
            </div>
            <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-extrabold tracking-wide text-ink">
              <Play className="h-3.5 w-3.5" strokeWidth={3} fill="currentColor" />
              {badge}
            </span>
            <span className="relative grid h-16 w-16 place-items-center rounded-full bg-chalk/95 shadow-xl transition motion-safe:group-hover:scale-105 md:h-[72px] md:w-[72px]">
              <Play className="ml-1 h-8 w-8 text-board" fill="currentColor" strokeWidth={1.5} />
            </span>
          </button>
        )}
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-soft">
        <Info className="h-4 w-4 flex-none" strokeWidth={2} />
        Ukázkovou lekci přehrajete hned, bez registrace.
      </p>
    </div>
  );
}
